import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Row, Column } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import {
  ToolCall,
  ToolCallResponse,
} from "@/shared/contexts/gptgateway.context";

interface ToolCallLoaderProps {
  toolCall?: ToolCall | ToolCallResponse | null;
  isThinking?: boolean;
}

// Helper function to convert snake_case to human-readable text
const formatToolName = (name: string): string => {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Animated dots component - fixed width, shows/hides dots
const AnimatedDots: React.FC = () => {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 0 : prev + 1));
    }, 700);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block w-[1.5ch]">
      <span className={dotCount >= 1 ? "visible" : "invisible"}>.</span>
      <span className={dotCount >= 2 ? "visible" : "invisible"}>.</span>
      <span className={dotCount >= 3 ? "visible" : "invisible"}>.</span>
    </span>
  );
};

export const ToolCallLoader: React.FC<ToolCallLoaderProps> = ({
  toolCall,
  isThinking = false,
}) => {
  if (!toolCall && !isThinking) return null;

  let displayText = "WisGPT is thinking";

  if (toolCall) {
    const isToolCall = "name" in toolCall;
    displayText = isToolCall
      ? formatToolName(toolCall.name)
      : "Processing results";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="w-full"
    >
      <Row className="w-full">
        <Column
          className={cn(
            "py-3 px-4 rounded-lg rounded-tl-none",
            "bg-gradient-to-r from-purple-50 to-purple-100",
            "border border-purple-200",
            "text-purple-700",
            "animate-pulse-subtle"
          )}
        >
          <Row className="gap-3 items-center">
            <div className="relative flex items-center justify-center w-6 h-6">
              {/* Main pulsing icon */}
              <Icon
                icon="Magicwand"
                size="sm"
                className="text-purple-500 animate-pulse relative z-10"
              />
            </div>
            <span className="text-sm font-medium">
              {displayText}
              <AnimatedDots />
            </span>
          </Row>
        </Column>
      </Row>
    </motion.div>
  );
};
